import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Coins } from 'lucide-react';
import { billingAPI } from '../../services/api';
import { useUserStore } from '../../stores/userStore';
import { BuyCreditsDialog } from '../billing/BuyCreditsDialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { aboutVideos } from '../../config/pricing';
import { studioBOn } from '../../lib/studioB/flag';

const ORG_HINT = "Your organization's admin adds credits";

function useCredits() {
  const client = useQueryClient();
  const balance = useQuery({ queryKey: ['billing', 'balance'], queryFn: billingAPI.getBalance, refetchOnWindowFocus: true });
  const lastTopUp = useQuery({
    queryKey: ['billing', 'last-top-up'],
    queryFn: async () => {
      const { entries } = await billingAPI.getActivity({ limit: 25 });
      return entries.find((e) => e.kind === 'purchase' || e.kind === 'grant')?.credits ?? null;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) client.invalidateQueries({ queryKey: ['billing'] });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [client]);

  return { balance: balance.data, topUpCredits: lastTopUp.data };
}

/** A narrow rail's balance: whole credits, in thousands from 1,000 (808, 12, 1.2k, 24k). */
const wholeCredits = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : `${Math.floor(n)}`);

/** `alert` is the pill shown only when the wallet is out of credits (Direction B keeps the balance in the account menu). */
export function CreditsMeter({ variant = 'rail' }: { variant?: 'rail' | 'pill' | 'icon' | 'alert' }) {
  const { balance, topUpCredits } = useCredits();
  const orgName = useUserStore((s) => s.activeOrganization?.name);
  const [buying, setBuying] = useState(false);

  if (!balance) return null;

  const isOrg = balance.scope === 'org';
  const out = balance.availableMilli <= 0;
  const credits = (balance.balanceMilli / 1000).toFixed(2);
  const label = isOrg ? `${orgName ?? 'Organization'} credits` : 'Credits';
  const about = out ? 'Out of credits' : aboutVideos(balance.availableMilli / 1000);
  const fill = topUpCredits ? Math.min(100, Math.max(0, balance.availableMilli / 10 / topUpCredits)) : null;
  const summary = [`${credits} credits`, about, isOrg && ORG_HINT].filter(Boolean).join(' · ');

  if (variant === 'icon' && studioBOn()) {
    return (
      <Link
        to="/settings/billing"
        aria-label={summary}
        title={summary}
        className={cn(
          'flex w-full flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-colors hover:bg-accent',
          out ? 'text-destructive' : 'text-muted-foreground hover:text-accent-foreground',
        )}
      >
        <Coins className="h-4 w-4" />
        <span className={cn('text-[11px] font-semibold leading-none tabular-nums', out ? 'text-destructive' : 'text-foreground')}>
          {out ? 'Out' : wholeCredits(balance.balanceMilli / 1000)}
        </span>
        {fill !== null && (
          <span aria-hidden="true" className="h-[3px] w-7 overflow-hidden rounded-full bg-border">
            <span data-fill className={cn('block h-full rounded-full', out ? 'bg-destructive' : 'bg-primary')} style={{ width: `${fill}%` }} />
          </span>
        )}
      </Link>
    );
  }

  if (variant === 'icon') {
    return (
      <Link
        to="/settings/billing"
        aria-label={summary}
        title={summary}
        className={cn(
          'grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-accent',
          out ? 'text-destructive' : 'text-muted-foreground hover:text-accent-foreground',
        )}
      >
        <Coins className="h-4 w-4" />
      </Link>
    );
  }

  if (variant === 'alert' && !out) return null;

  if (variant === 'pill' || variant === 'alert') {
    return (
      <Link
        to="/settings/billing"
        title={summary}
        data-credits-alert={variant === 'alert' || undefined}
        className={cn(
          'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5',
          out ? 'border-destructive/40 text-destructive' : 'border-border studio-text-secondary',
        )}
      >
        <Coins className="h-3.5 w-3.5" />
        <span className="tabular-nums">{out && !isOrg ? 'Get credits' : credits}</span>
      </Link>
    );
  }

  return (
    <div className="grid min-w-0 gap-2 rounded-xl border border-border bg-muted/40 p-3" title={isOrg ? ORG_HINT : undefined}>
      <Link to="/settings/billing" className="flex min-w-0 items-baseline justify-between gap-2 rounded-md hover:opacity-80" aria-label={`Billing & credits: ${summary}`}>
        <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground" title={label}>{label}</span>
        <span className={cn('shrink-0 font-bold tabular-nums', credits.length > 8 ? 'text-sm' : 'text-lg')} title={`${credits} credits`}>{credits}</span>
      </Link>
      {fill !== null && (
        <div className="h-1.5 overflow-hidden rounded-full bg-border">
          <div className={cn('h-full rounded-full', out ? 'bg-destructive' : 'bg-primary')} style={{ width: `${fill}%` }} />
        </div>
      )}
      {about && <span className={cn('truncate text-xs', out ? 'font-medium text-destructive' : 'text-muted-foreground')}>{about}{!out && ' at Pro'}</span>}
      {!isOrg && (
        <>
          <Button size="sm" className="h-8 w-full" onClick={() => setBuying(true)}>
            {out ? 'Get credits' : 'Buy credits'}
          </Button>
          <BuyCreditsDialog open={buying} onOpenChange={setBuying} />
        </>
      )}
    </div>
  );
}
